import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Timer, Sparkles, Building } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";
import { fetchProducts } from "../lib/api";

const VERTICALS = [
  { Icon: Building, title: "Boutique Resorts", desc: "8–40 key masterplanned clusters, phased to your capital plan." },
  { Icon: Sparkles, title: "Glamping & Eco Retreats", desc: "Capsules, A-frames and villages with minimal land disturbance." },
  { Icon: Timer, title: "Hotel Expansions", desc: "Add premium inventory without closing a single existing room." },
  { Icon: TrendingUp, title: "Wellness Destinations", desc: "Pavilions, spa pods and suites built around stillness." },
];

export default function Hospitality() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts({ category: "hospitality-resort" }).then(setProducts).catch(() => {});
  }, []);

  return (
    <PageWrapper>
      {/* Hero */}
      <section className="relative pt-[76px]" data-testid="hospitality-hero">
        <div className="relative h-[440px] md:h-[540px] overflow-hidden">
          <img src="/images/gen/resort-aerial.jpg" alt="NUVUN resort at dusk" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/55 to-[#0A0908]/25" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 pb-12 w-full">
              <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">Hospitality & Retreats</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3] max-w-3xl">
                Room inventory for the <span className="italic text-[#D9A441]">experience economy.</span>
              </h1>
              <p className="mt-4 text-sm md:text-base text-[#F2EDE3]/60 max-w-xl">
                Factory-built resorts that open in months, not years — and start earning
                the season you break ground.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/contact?enquiry_type=hospitality" data-testid="hospitality-hero-cta" className="btn-gold">
                  Discuss Your Property <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/projects" data-testid="hospitality-projects-cta" className="btn-ghost">
                  See Delivered Resorts
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI stats */}
      <section className="border-b border-[#D9A441]/15" data-testid="hospitality-stats">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#D9A441]/12">
          {[["5 mo", "Land to first guest"], ["30–45%", "Lower build cost / key"], ["2–3 yr", "Typical payback"], ["₹18K+", "ADR on capsule suites"]].map(([v, l]) => (
            <div key={l} className="py-8 text-center">
              <div className="font-condensed text-3xl md:text-4xl text-[#D9A441]">{v}</div>
              <div className="eyebrow !text-[9px] text-[#F2EDE3]/50 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Verticals */}
      <section className="py-20 lg:py-24" data-testid="hospitality-verticals">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp>
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">What We Build</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F2EDE3]">Four hospitality verticals.</h2>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VERTICALS.map(({ Icon, title, desc }, i) => (
              <FadeUp key={title} delay={i * 0.06}>
                <div className="panel p-7 h-full hover:border-[#D9A441]/50 transition-colors duration-500">
                  <span className="w-11 h-11 rounded-full border border-[#D9A441]/45 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#D9A441]" />
                  </span>
                  <div className="font-display text-xl text-[#F2EDE3]">{title}</div>
                  <p className="text-sm text-[#F2EDE3]/55 mt-3 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Inventory showcase */}
      <section className="pb-20 lg:pb-24" data-testid="hospitality-showcase">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">The Inventory</div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#F2EDE3]">Hospitality &amp; Resort collection.</h2>
            </div>
            <Link to="/products/category/hospitality-resort" data-testid="hospitality-view-all" className="btn-ghost">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.slice(0, 6).map((p, i) => (
              <FadeUp key={p.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/products/${p.slug}`}
                  data-testid={`hospitality-product-${p.slug}`}
                  className="group block panel overflow-hidden hover:border-[#D9A441]/50 transition-colors duration-500"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={p.image} alt={p.name} loading="lazy" className="product-card-img absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/70 to-transparent" />
                    <span className="absolute bottom-4 right-4 eyebrow !text-[9px] bg-[#D9A441] text-[#0A0908] px-3 py-1.5 rounded-sm">
                      {p.price_range}
                    </span>
                  </div>
                  <div className="p-6 flex items-center justify-between gap-4">
                    <div>
                      <div className="font-display text-xl text-[#F2EDE3] group-hover:text-[#D9A441] transition-colors">{p.name}</div>
                      <div className="text-xs text-[#F2EDE3]/50 mt-1.5">{p.form} · {p.deployment}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#D9A441] shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Suite interior + CTA */}
      <section className="border-t border-[#D9A441]/12" data-testid="hospitality-cta">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-6">
            <div className="overflow-hidden rounded-lg gold-frame">
              <img src="/images/gen/interior-luxury.jpg" alt="NUVUN suite interior" loading="lazy" className="w-full h-[300px] md:h-[420px] object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-5 lg:col-start-8">
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">Turnkey Development</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F2EDE3] leading-[1.15]">
              From bare land to first check-in —<br />
              <span className="italic text-[#D9A441]">one partner.</span>
            </h2>
            <p className="mt-5 text-sm text-[#F2EDE3]/60 leading-relaxed">
              Masterplanning, unit manufacturing, common areas, interiors and handover —
              phased to your capital and your season. Share your land details, and we'll
              respond with a feasibility snapshot within a week.
            </p>
            <Link to="/contact?enquiry_type=hospitality" data-testid="hospitality-bottom-cta" className="btn-gold mt-8">
              Let&apos;s Build Together <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
